import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Field, Datepicker, ButtonIcon } from 'components';
import Select from '../Select/Select';
import {
  FilterRow,
  FilterInput,
  FilterSelect,
  FilterDatepicker,
  FilterButton,
  DateRange,
  DateRangeButton,
} from './ColumnFilter.styled';
import { formatDate } from 'helpers/date';

const ClearIcon = ({ onClick }) => (
  <ButtonIcon icon="close" secondary onClick={onClick} style={FilterButton} title="Clear" />
);
const ApplyIcon = ({ onClick }) => (
  <ButtonIcon icon="check" secondary onClick={onClick} style={FilterButton} title="Apply" />
);

const useClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (
        !ref.current ||
        ref.current.contains(event.target) ||
        (event.target.closest && event.target.closest('#calendar-portal'))
      ) {
        return;
      }
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
    };
  }, [ref, handler]);
};

const ColumnFilter = ({
  type,
  value,
  valueFrom,
  valueTo,
  valueYear,
  onChange,
  onChangeFrom,
  onChangeTo,
  onChangeYear,
  options,
  placeholder,
  small,
  id,
  appliedValue,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [dateFromValue, setDateFromValue] = useState(valueFrom || '');
  const [dateToValue, setDateToValue] = useState(valueTo || '');
  const [open, setOpen] = useState(false);
  const popupRef = useRef(null);
  const [selectedYear, setSelectedYear] = useState(valueYear || '');

  useClickOutside(popupRef, () => setOpen(false));

  useEffect(() => {
    if (type === 'input') setInputValue(value || '');
    if (type === 'datepicker-range') {
      setDateFromValue(valueFrom || '');
      setDateToValue(valueTo || '');
      setSelectedYear(valueYear || '');
    }
  }, [value, valueFrom, valueTo, type, valueYear]);

  if (type === 'select') {
    return (
      <Field id={id} small={small} style={{ margin: 0 }}>
        <FilterRow>
          <FilterSelect
            id={id}
            name={id}
            value={options.find((opt) => opt.value === value) || null}
            onChange={(opt) => onChange(opt ? opt.value : '')}
            options={options}
            placeholder={placeholder}
            small={small}
          />
          {value ? <ClearIcon onClick={() => onChange('')} /> : null}
        </FilterRow>
      </Field>
    );
  }

  const handleChangeDateFrom = (date) => {
    setDateFromValue(date);
    onChangeFrom(date);
  };

  const handleChangeDateTo = (date) => {
    setDateToValue(date);
    onChangeTo(date);
  };

  const handleChangeYear = (val) => {
    setSelectedYear(val);
    onChangeYear(val);
  };

  const handleClear = () => {
    handleChangeDateFrom('');
    handleChangeDateTo('');
    handleChangeYear('');
  };

  if (type === 'datepicker') {
    return (
      <Field id={id} small={small} style={{ margin: 0 }}>
        <FilterRow>
          <FilterDatepicker
            id={id}
            name={id}
            selected={value}
            onChange={onChange}
            placeholderText={placeholder}
            showYearDropdown
            showMonthDropdown
            dateFormat="dd/MM/yyyy"
            small={small}
          />
          {value ? <ClearIcon onClick={() => onChange('')} /> : null}
        </FilterRow>
      </Field>
    );
  }

  if (type === 'datepicker-range' || type === 'datepicker-range-year-select') {
    const hasValue =
      valueFrom || valueTo || (type === 'datepicker-range-year-select' && selectedYear);
    const label = (date) => formatDate(date) || '';
    const displayValue =
      valueFrom && valueTo
        ? `${label(valueFrom)} - ${label(valueTo)}`
        : valueFrom || valueTo
        ? `${label(valueFrom)}${valueTo ? ' - ' + label(valueTo) : ''}`
        : placeholder;
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 6 }, (_, i) => (currentYear - 5 + i).toString());
    const yearOptions = years.map((year) => ({ value: year, label: year }));
    return (
      <div style={{ position: 'relative', margin: 0 }}>
        <Field id={id} small={small}>
          <FilterRow>
            <DateRangeButton type="button" onClick={() => setOpen((v) => !v)}>
              {displayValue}
            </DateRangeButton>
            {hasValue ? <ClearIcon onClick={handleClear} /> : null}
          </FilterRow>
        </Field>
        {open && (
          <DateRange ref={popupRef}>
            <Datepicker
              selected={dateFromValue}
              onChange={(date) => {
                handleChangeDateFrom(date);
                handleChangeYear('');
              }}
              placeholderText={`${placeholder} From`}
              showYearDropdown
              showMonthDropdown
              dateFormat="dd/MM/yyyy"
              maxDate={dateToValue}
              small
            />
            <Datepicker
              selected={dateToValue}
              onChange={(date) => {
                handleChangeDateTo(date);
                handleChangeYear('');
              }}
              placeholderText={`${placeholder} To`}
              showYearDropdown
              showMonthDropdown
              dateFormat="dd/MM/yyyy"
              minDate={dateFromValue}
              small
            />
            {type === 'datepicker-range-year-select' && (
              <Select
                small
                value={selectedYear ? { value: selectedYear, label: selectedYear } : null}
                onChange={(option) => {
                  const val = option ? option.value : '';
                  handleChangeYear(val);
                  handleChangeDateFrom('');
                  handleChangeDateTo('');
                }}
                options={yearOptions}
                placeholder="Year"
                isClearable
                menuPlacement="auto"
              />
            )}
          </DateRange>
        )}
      </div>
    );
  }

  const showInputClear = appliedValue && inputValue === appliedValue;
  return (
    <Field id={id} small={small} style={{ margin: 0 }}>
      <FilterRow>
        <FilterInput
          id={id}
          name={id}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onChange(inputValue);
            }
          }}
          placeholder={placeholder}
          small={small}
        />
        {showInputClear ? (
          <ClearIcon
            onClick={() => {
              setInputValue('');
              onChange('');
            }}
          />
        ) : null}
        <ApplyIcon onClick={() => onChange(inputValue)} />
      </FilterRow>
    </Field>
  );
};

ColumnFilter.propTypes = {
  type: PropTypes.oneOf(['input', 'select', 'datepicker', 'datepicker-range']),
  value: PropTypes.any,
  valueFrom: PropTypes.any,
  valueTo: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  onChangeFrom: PropTypes.func,
  onChangeTo: PropTypes.func,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  small: PropTypes.bool,
  id: PropTypes.string,
  appliedValue: PropTypes.string,
  valueYear: PropTypes.string,
  onChangeYear: PropTypes.func,
};

ColumnFilter.defaultProps = {
  type: 'input',
  value: '',
  valueFrom: '',
  valueTo: '',
  options: [],
  onChangeFrom: () => {},
  onChangeTo: () => {},
  placeholder: '',
  small: true,
  id: undefined,
  appliedValue: '',
  valueYear: '',
  onChangeYear: () => {},
};

export default ColumnFilter;
